import { type FC, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { cn } from "@/components/podkit/lib/cn";
import { ScrollArea } from "@/components/flexkit/ScrollArea";

type MarkdownProps = {
    content: string;
    className?: string;
};

const MarkdownBase: FC<MarkdownProps> = ({ content, className = "", ...props }) => {
    if (!content) return null;

    // This implements the blinking cursor effect for text that is being streamed in.
    //
    // At a high level the technique is to use a :after pseudo element to style an inline-block after the last child of the last child.
    // The block is then styled to look like a cursor and the blinking is achieved using a simple opacity-based animation. When we're done
    // streaming, we fade out the cursor.
    //
    // Some notes on the animation:
    //
    // - We're using a delay of 300ms so that the cursor only starts blinking if there's a streaming pause of at least 300ms. Because ReactMarkdown
    //   re-renders the node when we change the content, this means the delay is reset whenever the content changes.
    //
    // - When a block is completed, we're fading out the cursor. This is done by setting the data-completed attribute to true on the parent,
    //   which we then use to selectively modify the background color from secondary (when it's not completed) to transparent (when it is).
    //   We're using a data-attribute rahter than a prop to this component becuase ReactMarkdown performs excessive re-renders which replaces DOM nodes
    //   and breaks the cursor effect.
    //
    const textClassName = [
        // The [.markdown-container>&:last-child] selector targets
        // - The last child in the group
        // - the last child inside of *that* element
        // Which ensures that we only add the cursor (|) after the very last character of the entire message
        //
        // The [[data-completed=false]_.markdown-container>&:last-child] selector does the same,
        // but only when the data-completed attribute it set to false on the parent.
        //
        "[.markdown-container>&:last-child]:after:transition-colors [.markdown-container>&:last-child]:after:[transition-duration:300ms] [.markdown-container>&:last-child]:after:bg-transparent [.markdown-container>&:last-child]:after:content-[' '] [.markdown-container>&:last-child]:after:inline-block [.markdown-container>&:last-child]:after:h-[0.8lh] [.markdown-container>&:last-child]:after:ml-[1px] [.markdown-container>&:last-child]:after:w-[6px] [.markdown-container>&:last-child]:after:rounded-sm [.markdown-container>&:last-child]:after:align-text-top",
        "[[data-completed=false]_.markdown-container>&:last-child]:after:bg-content-secondary [[data-completed=false]_.markdown-container>&:last-child]:after:animate-blink [[data-completed=false]_.markdown-container>&:last-child]:after:delay-300",
    ].join(" ");

    const components: Components = {
        h1: ({ children, ...props }) => (
            <h1 className={cn("mb-4 border-b border-gray-200 pb-2 text-xl font-semibold", textClassName)} {...props}>
                {children}
            </h1>
        ),
        h2: ({ children, ...props }) => (
            <h2 className={cn("mb-3 border-b border-gray-200 pb-2 text-lg font-semibold", textClassName)} {...props}>
                {children}
            </h2>
        ),
        h3: ({ children, ...props }) => (
            <h3 className={cn("text-md mb-3 font-semibold", textClassName)} {...props}>
                {children}
            </h3>
        ),
        ul: ({ children, ...props }) => (
            <ul className={cn("mb-4 list-disc pl-8", textClassName)} {...props}>
                {children}
            </ul>
        ),
        ol: ({ children, ...props }) => (
            <ol className={cn("mb-4 list-decimal pl-8", textClassName)} {...props}>
                {children}
            </ol>
        ),
        li: ({ children, ...props }) => (
            <li className={cn("mb-1", textClassName)} {...props}>
                {children}
            </li>
        ),
        a: ({ children, ...props }) => (
            <a className={cn("text-blue-600 hover:underline", textClassName)} {...props}>
                {children}
            </a>
        ),
        blockquote: ({ children, ...props }) => (
            <blockquote
                className={cn("mb-4 border-l-4 border-gray-300 py-1 pl-4 text-gray-700", textClassName)}
                {...props}
            >
                {children}
            </blockquote>
        ),
        table: ({ children, ...props }) => (
            <div className="mb-4 overflow-x-auto">
                <table className="min-w-full border border-gray-300" {...props}>
                    {children}
                </table>
            </div>
        ),
        thead: ({ children, ...props }) => (
            <thead className="border-b border-gray-300 bg-gray-100" {...props}>
                {children}
            </thead>
        ),
        th: ({ children, ...props }) => (
            <th className={cn("px-4 py-2 text-left text-sm font-semibold", textClassName)} {...props}>
                {children}
            </th>
        ),
        td: ({ children, ...props }) => (
            <td className={cn("border-t border-gray-300 px-4 py-2 text-sm", textClassName)} {...props}>
                {children}
            </td>
        ),
        p: ({ node, children, ...props }) => {
            type MarkdownNode = {
                tagName?: string;
            };
            const childNodes = node?.children as MarkdownNode[];
            if (childNodes?.[0]?.tagName === "img" && childNodes.length === 1) {
                return <>{children}</>;
            }
            return (
                <p className={cn("mb-4 last:mb-0", textClassName)} {...props}>
                    {children}
                </p>
            );
        },
        code: ({ children, ...props }) => {
            return (
                <code
                    className={cn("rounded-sm border-0.5 border-border-base p-1 font-mono", textClassName)}
                    {...props}
                >
                    {children}
                </code>
            );
        },
        pre: ({ children, ...props }) => {
            return (
                <pre
                    className={cn(
                        "mb-4 rounded-md border border-border-base px-2 pb-1 pt-2 font-mono text-sm text-content-secondary last:mb-0",
                        textClassName,
                    )}
                    {...props}
                >
                    <ScrollArea orientation="horizontal">{children}</ScrollArea>
                </pre>
            );
        },
        img: ({ ...props }) => <img className="my-2 h-auto max-w-full rounded-md" {...props} alt={props.alt || ""} />,
        hr: () => <hr className="my-6 border-t border-gray-300" />,
        strong: ({ children, ...props }) => (
            <strong className={cn("font-semibold", textClassName)} {...props}>
                {children}
            </strong>
        ),
        em: ({ children, ...props }) => (
            <em className={cn("italic", textClassName)} {...props}>
                {children}
            </em>
        ),
    };

    return (
        <div
            className={cn(
                "markdown-container",
                /* Break at word boundaries */
                "[word-break:break-word]",
                /* Fallback: Break at any character. This acts a fallback as it only triggers if the text would otherwise overflow */
                "[overflow-wrap:anywhere]",
                className,
            )}
        >
            <ReactMarkdown components={components} {...props}>
                {content}
            </ReactMarkdown>
        </div>
    );
};

/**
 * Memoized version of Markdown that only re-renders when the content changes.
 * This is needed as the ReactMarkdown libarary we're using doesn't handle content changes well -
 * - it would rerender even when provided with the exact same content.
 */
export const Markdown = memo(MarkdownBase, (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
});
